import React, { useState } from 'react';
import { MessageSquarePlus, BookOpen, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BookClubDiscussion } from '@/types/chat';
import { DiscussionCard } from '@/components/DiscussionCard';
import { CreateDiscussionDialog } from '@/components/CreateDiscussionDialog';
import { useBookClubDiscussions } from '@/hooks/useBookClubDiscussions';

interface BookClubDiscussionsPanelProps {
  chatId: string;
}

export function BookClubDiscussionsPanel({ chatId }: BookClubDiscussionsPanelProps) {
  const {
    discussions,
    loading,
    createDiscussion,
    deleteDiscussion,
    fetchReplies,
    addReply,
    deleteReply,
  } = useBookClubDiscussions(chatId);
  const [showCreate, setShowCreate] = useState(false);
  const [createType, setCreateType] = useState<'prompt' | 'chapter'>('prompt');

  const prompts = discussions.filter(d => d.type === 'prompt');
  const chapters = discussions.filter(d => d.type === 'chapter');

  const handleCreate = (type: 'prompt' | 'chapter') => {
    setCreateType(type);
    setShowCreate(true);
  };

  if (loading) {
    return (
      <div className="p-4 text-center text-muted-foreground text-sm">
        Loading discussions...
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="font-heading font-semibold text-lg">Discussions</h3>
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCreate('prompt')}
            className="gap-1.5"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Prompt
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCreate('chapter')}
            className="gap-1.5"
          >
            <BookOpen className="w-3.5 h-3.5" />
            Chapter
          </Button>
        </div>
      </div>

      <Tabs defaultValue="all" className="flex-1 flex flex-col">
        <TabsList className="mx-4 mt-2">
          <TabsTrigger value="all">All ({discussions.length})</TabsTrigger>
          <TabsTrigger value="prompts">Prompts ({prompts.length})</TabsTrigger>
          <TabsTrigger value="chapters">Chapters ({chapters.length})</TabsTrigger>
        </TabsList>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <TabsContent value="all" className="mt-0 space-y-3">
            {discussions.length === 0 ? (
              <EmptyState onCreatePrompt={() => handleCreate('prompt')} onCreateChapter={() => handleCreate('chapter')} />
            ) : (
              discussions.map(d => (
                <DiscussionCard
                  key={d.id}
                  discussion={d}
                  onDelete={deleteDiscussion}
                  onFetchReplies={fetchReplies}
                  onAddReply={addReply}
                  onDeleteReply={deleteReply}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="prompts" className="mt-0 space-y-3">
            {prompts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No discussion prompts yet. Start one!
              </p>
            ) : (
              prompts.map(d => (
                <DiscussionCard
                  key={d.id}
                  discussion={d}
                  onDelete={deleteDiscussion}
                  onFetchReplies={fetchReplies}
                  onAddReply={addReply}
                  onDeleteReply={deleteReply}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="chapters" className="mt-0 space-y-3">
            {chapters.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No chapter threads yet. Create one to discuss specific sections!
              </p>
            ) : (
              chapters.map(d => (
                <DiscussionCard
                  key={d.id}
                  discussion={d}
                  onDelete={deleteDiscussion}
                  onFetchReplies={fetchReplies}
                  onAddReply={addReply}
                  onDeleteReply={deleteReply}
                />
              ))
            )}
          </TabsContent>
        </div>
      </Tabs>

      <CreateDiscussionDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        type={createType}
        onSubmit={createDiscussion}
      />
    </div>
  );
}

function EmptyState({
  onCreatePrompt,
  onCreateChapter,
}: {
  onCreatePrompt: () => void;
  onCreateChapter: () => void;
}) {
  return (
    <div className="text-center py-12 space-y-4">
      <MessageSquarePlus className="w-10 h-10 mx-auto text-muted-foreground/50" />
      <div>
        <p className="font-medium text-foreground">No discussions yet</p>
        <p className="text-sm text-muted-foreground mt-1">
          Start a discussion prompt or create a chapter thread to get the conversation going.
        </p>
      </div>
      <div className="flex gap-2 justify-center">
        <Button variant="outline" size="sm" onClick={onCreatePrompt} className="gap-1.5">
          <Sparkles className="w-3.5 h-3.5" />
          Add a Prompt
        </Button>
        <Button variant="outline" size="sm" onClick={onCreateChapter} className="gap-1.5">
          <BookOpen className="w-3.5 h-3.5" />
          Add Chapter Thread
        </Button>
      </div>
    </div>
  );
}
